import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Download } from "lucide-react";
import { toast } from "sonner";
import dayjs from "dayjs";

const satBadge = (s) => {
  if (s === "positive") return { cls: "bg-emerald-100 text-emerald-800", label: "Puas" };
  if (s === "neutral") return { cls: "bg-amber-100 text-amber-800", label: "Biasa" };
  return { cls: "bg-red-100 text-red-800", label: "Kurang Puas" };
};

const filters = [
  { key: "all", label: "Semua" },
  { key: "positive", label: "Puas" },
  { key: "neutral", label: "Biasa" },
  { key: "negative", label: "Kurang Puas" },
];

export default function FeedbackList() {
  const { user } = useAuth();
  const isSuper = user?.role === "super_admin";
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState("");

  const download = async (period) => {
    setExporting(period);
    try {
      const res = await api.get(`/feedback/export?period=${period}`, { responseType: "blob" });
      const dispo = res.headers["content-disposition"] || "";
      const match = dispo.match(/filename="?([^";]+)"?/);
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = match ? match[1] : `feedback-${period}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("File Excel berhasil diunduh");
    } catch {
      toast.error("Gagal mengunduh export");
    } finally { setExporting(""); }
  };

  const load = async (f = filter) => {
    setLoading(true);
    const { data } = await api.get(`/feedback?satisfaction=${f}`);
    setItems(data); setLoading(false);
  };

  useEffect(() => { load(filter); /* eslint-disable-next-line */ }, [filter]);

  const changeStatus = async (id, status) => {
    try {
      await api.patch(`/feedback/${id}/status`, { status });
      toast.success(`Status diperbarui: ${status}`);
      setItems(prev => prev.map(x => x.id === id ? { ...x, status } : x));
    } catch (e) {
      toast.error("Gagal mengubah status");
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">Daftar Feedback</h1>
          <p className="text-sm text-zinc-500 mt-1">Semua masukan dari pelanggan.</p>
        </div>
        {isSuper && (
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => download("today")} disabled={!!exporting} data-testid="export-today-button">
              <Download size={14} className="mr-1.5" /> {exporting === "today" ? "Mengunduh..." : "Download Hari Ini"}
            </Button>
            <Button variant="outline" onClick={() => download("week")} disabled={!!exporting} data-testid="export-week-button">
              <Download size={14} className="mr-1.5" /> {exporting === "week" ? "Mengunduh..." : "Download Minggu Ini"}
            </Button>
          </div>
        )}
      </div>

      <div className="flex flex-wrap gap-2">
        {filters.map(f => (
          <button key={f.key} onClick={() => setFilter(f.key)}
            data-testid={`filter-${f.key}`}
            className={`px-4 py-2 rounded-xl text-sm font-medium border transition-colors ${filter === f.key ? "bg-emerald-600 text-white border-emerald-600" : "bg-white text-zinc-700 border-zinc-200 hover:border-emerald-500"}`}>
            {f.label}
          </button>
        ))}
      </div>

      <div className="bg-white border border-zinc-200 rounded-2xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-zinc-50 text-zinc-500 text-xs uppercase tracking-wider">
              <tr>
                <th className="text-left px-5 py-3 font-semibold">Pelanggan</th>
                <th className="text-left px-5 py-3 font-semibold">WhatsApp</th>
                <th className="text-left px-5 py-3 font-semibold">Kepuasan</th>
                <th className="text-left px-5 py-3 font-semibold">Kategori</th>
                <th className="text-left px-5 py-3 font-semibold">Pesan</th>
                <th className="text-left px-5 py-3 font-semibold">Tanggal</th>
                <th className="text-left px-5 py-3 font-semibold">Status</th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={7} className="px-5 py-8 text-center text-zinc-400">Memuat...</td></tr>}
              {!loading && items.length === 0 && <tr><td colSpan={7} className="px-5 py-8 text-center text-zinc-400">Tidak ada data.</td></tr>}
              {items.map(f => {
                const b = satBadge(f.satisfaction);
                return (
                  <tr key={f.id} className="border-t border-zinc-100 align-top" data-testid={`feedback-row-${f.id}`}>
                    <td className="px-5 py-3 font-medium">{f.name || <span className="text-zinc-400">Anonim</span>}</td>
                    <td className="px-5 py-3 text-zinc-600">{f.whatsapp || "—"}</td>
                    <td className="px-5 py-3"><Badge className={`${b.cls} border-0`}>{b.label}</Badge></td>
                    <td className="px-5 py-3 text-zinc-600">{f.category || "—"}</td>
                    <td className="px-5 py-3 text-zinc-700 max-w-xs">{f.message || "—"}</td>
                    <td className="px-5 py-3 text-zinc-500 whitespace-nowrap">{dayjs(f.created_at).format("DD MMM HH:mm")}</td>
                    <td className="px-5 py-3">
                      {f.satisfaction === "negative" ? (
                        <Select value={f.status || "Baru"} onValueChange={(v) => changeStatus(f.id, v)}>
                          <SelectTrigger className="h-8 text-xs w-32" data-testid={`status-select-${f.id}`}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="Baru">Baru</SelectItem>
                            <SelectItem value="Diproses">Diproses</SelectItem>
                            <SelectItem value="Selesai">Selesai</SelectItem>
                          </SelectContent>
                        </Select>
                      ) : <span className="text-zinc-400 text-xs">—</span>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
